import React from 'react';
import { PageProps } from 'gatsby';
import classNames from 'classnames';
import { InputNames } from '@/types';
import YoutubeIcon from '@/images/svgs/youtube.svg';
import SupportIcon from '@/images/svgs/support.svg';
import { useContactForm } from '@/hooks/useContactForm';
import { useStyles } from '@/hooks/useStyles';
import Notification from '@/components/notification/notification';

const ContactForm: React.FC<PageProps> = () => {
  const { joinClasses } = useStyles();

  const sectionClasses =
    'group overflow-hidden relative py-[1.5rem] after:bg-black/50 after:absolute after:top-0 after:left-0 after:w-full after:h-full';
  const bgClasses =
    'absolute w-full h-full top-0 left-0 transition duration-1000 scale-125 group-hover:scale-100 bg-contact-form bg-cover bg-no-repeat bg-center';
  const labelClasses = 'text-white flex flex-col';
  const inputClasses =
    'w-full backdrop-blur-sm bg-white/30 border-b border-white px-[0.625rem] py-[0.25rem] outline-none min-h-[2.875rem] text-input';
  const buttonClasses = classNames(
    'col-span-12 inline-block w-full justify-self-end not(:disabled):cursor-pointer mt-4 bg-white text-primary px-[1.88rem] py-[0.62rem] sm:max-w-[16.25rem] rounded-2xl text-button',
    'disabled:opacity-75',
    'hover:[&:not(:disabled)]:bg-primary hover:[&:not(:disabled)]:text-white disabled:cursor-not-allowed'
  );

  const inputsList = [
    {
      required: true,
      type: 'text',
      name: InputNames.FIRST_NAME,
      label: 'First Name',
      classes: 'col-span-12 md:col-span-6'
    },
    {
      required: true,
      type: 'text',
      name: InputNames.LAST_NAME,
      label: 'Last Name',
      classes: 'col-span-12 md:col-span-6'
    },
    {
      required: true,
      type: 'email',
      name: InputNames.EMAIL,
      label: 'Email',
      classes: 'col-span-12'
    },
    {
      type: 'tel',
      name: InputNames.MOBILE_PHONE,
      label: 'Phone Number',
      classes: 'col-span-12'
    },
    {
      type: 'text',
      name: InputNames.COMPANY,
      label: 'Company Name',
      classes: 'col-span-12'
    },
    {
      type: 'textarea',
      name: InputNames.MESSAGE,
      label: 'Message',
      classes: 'col-span-12'
    }
  ];

  const {
    formState: { errors, isValid, isSubmitting },
    notification,
    clearNotification,
    handleSubmit,
    register
  } = useContactForm();

  const inputFiledClasses = (type: string, name: InputNames): string =>
    classNames(inputClasses, {
      'resize-none border': type === 'textarea',
      '!border-red-500': !!errors[name]
    });

  const spanClasses = (required?: boolean, isLabel?: boolean): string =>
    classNames('my-1 text-label text-white', {
      "after:content-['*'] after:pl-[0.625rem]": required,
      "font-['SegoeUIBold']": !isLabel,
      "font-['KohinoorBanglaMedium']": isLabel
    });

  const contactSubsections = (subsection: string): string =>
    classNames('z-10 relative  content-center text-white', {
      'lg:w-6/12 lg:mb-0': subsection == 'form',
      'inline-flex flex-col justify-center mx-auto': subsection == 'help'
    });

  const getInputField = ({ type, name }: { type: string; name: InputNames }) =>
    type != 'textarea' ? (
      <>
        <input
          {...register(name)}
          aria-invalid={!!errors[name]}
          className={inputFiledClasses(type, name)}
        />
      </>
    ) : (
      <textarea
        {...register(name)}
        className={inputFiledClasses(type, name)}
        rows={4}
        aria-invalid={!!errors.message}
      ></textarea>
    );

  const desktopClasses = (subsection?: string) => ({
    container: classNames('lg:flex-row'),
    form: contactSubsections(subsection)
  });
  const mobileClasses = {
    container: classNames('wrapper flex flex-col justify-between'),
    form: 'w-full grid grid-cols-12 gap-2 mb-[5rem]'
  };

  return (
    <>
      <section id="contact-form" className={sectionClasses}>
        {notification && (
          <Notification {...notification} close={clearNotification} />
        )}
        <div className={bgClasses}></div>
        <div
          className={joinClasses([
            desktopClasses().container,
            mobileClasses.container
          ])}
        >
          <form
            onSubmit={handleSubmit}
            className={joinClasses([
              desktopClasses('form').form,
              mobileClasses.form
            ])}
          >
            <h1 className="text-title-2 font-['SegoeUIBold'] text-white mb-[0.31rem] col-span-12">
              Request a demo
            </h1>
            {inputsList.map(({ required, type, name, label, classes }) => (
              <label className={labelClasses + ' ' + classes} key={name}>
                <span className={spanClasses(required, true)}>{label}</span>
                {getInputField({ type, name })}
              </label>
            ))}
            <input
              type="submit"
              value="Submit"
              disabled={!isValid || isSubmitting}
              className={buttonClasses}
            />
          </form>
          <div id="help-support" className={contactSubsections('help')}>
            <a
              href="https://helpdesk.serviceportal.airstagecloud.net/servicedesk/customer/portals"
              target="_blank"
              rel="noreferrer"
              className="border border-white bg-white/50 rounded-[1.25rem] mb-[2.188rem] py-[2.66rem] px-[5.13rem] flex flex-col content-center justify-center cursor-pointer hover:bg-primary/50"
            >
              <SupportIcon className="mx-auto" />
              <h2 className="font-['SegoeUIBold'] uppercase text-white text-xxl text-center">
                Help & Support
              </h2>
            </a>
            <a
              href="tel:+18005739101"
              target="_blank"
              rel="noreferrer"
              className="block flex flex-col text-center mb-[2.188rem]"
            >
              <span className={spanClasses()}>Phone</span>
              <span
                className={
                  spanClasses() + ' !font-["KohinoorBanglaLight"] mt-[1.25rem]'
                }
              >
                +1-800-573-9101
              </span>
            </a>
            <a
              href="https://www.youtube.com/@AIRSTAGECloud"
              rel="noreferrer"
              target="_blank"
              className="block flex flex-col text-center"
            >
              <span className={spanClasses()}>YouTube</span>
              <YoutubeIcon className="mx-auto mt-[1.25rem]" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactForm;
